import { useState } from "react";

interface ModalInput {
  name: string;
  type: "text" | "email" | "password" | "number";
  placeholder: string;
  value?: string;
}

interface ModalButton {
  text: string;
  role?: "cancel" | "destructive";
  handler?: (data?: any) => void;
}

interface ModalConfig {
  header?: string;
  message?: string;
  inputs?: ModalInput[];
  buttons?: ModalButton[];
}

export const useCustomModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState<ModalConfig>({});

  const showModal = (modalConfig: ModalConfig) => {
    setConfig(modalConfig);
    setIsOpen(true);
  };

  const hideModal = () => {
    setIsOpen(false);
    // Clear config after a delay to allow closing animation
    setTimeout(() => {
      setConfig({});
    }, 300);
  };

  // Convenience methods for common modal types
  const showAlert = (
    header: string,
    message: string,
    onConfirm?: () => void,
  ) => {
    showModal({
      header,
      message,
      buttons: [
        {
          text: "OK",
          handler: () => {
            if (onConfirm) onConfirm();
            hideModal();
          },
        },
      ],
    });
  };

  const showConfirm = (
    header: string,
    message: string,
    onConfirm?: () => void,
    onCancel?: () => void,
  ) => {
    showModal({
      header,
      message,
      buttons: [
        {
          text: "Annulla",
          role: "cancel",
          handler: () => {
            if (onCancel) onCancel();
            hideModal();
          },
        },
        {
          text: "Conferma",
          handler: () => {
            if (onConfirm) onConfirm();
            hideModal();
          },
        },
      ],
    });
  };

  const showPrompt = (
    header: string,
    message: string,
    inputs: ModalInput[],
    onConfirm?: (data: any) => void,
    onCancel?: () => void,
  ) => {
    showModal({
      header,
      message,
      inputs,
      buttons: [
        {
          text: "Annulla",
          role: "cancel",
          handler: () => {
            if (onCancel) onCancel();
            hideModal();
          },
        },
        {
          text: "Conferma",
          handler: (data) => {
            if (onConfirm) onConfirm(data);
            hideModal();
          },
        },
      ],
    });
  };

  return {
    isOpen,
    config,
    showModal,
    hideModal,
    showAlert,
    showConfirm,
    showPrompt,
  };
};
